import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  useItineraryStore,
  useGlobalCurrencyStore,
} from '@apps/main/new-desktop/core/stores';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';

import type {
  ContentApiImage,
  SmartSearchImageUrl,
} from '@libs/trip-planner-interfaces';

import { useBrowserStore } from '../../../core/stores/browserStore';
import type {
  HotelStaticData,
  HotelDynamicData,
  HotelDetailsFooterData,
  HotelDetailsGalleryData,
  HotelDetailsHeaderData,
  Stay,
} from '../models';
import { GalleryType } from '../models';

import {
  getFooterData,
  getGalleryData,
  getSelectedStay,
  getStays,
} from './hotelDetailsPanelStoreLogic';

export const useHotelDetailsPanelStore = defineStore(
  'HotelDetailsPanelStore',
  () => {
    const browserStore = useBrowserStore();
    const preferredTimeStore = usePreferredTimeStore();
    const itineraryStore = useItineraryStore();
    const globalCurrencyStore = useGlobalCurrencyStore();

    // State
    const hotelStaticData = ref<HotelStaticData | undefined>();
    const hotelDynamicData = ref<HotelDynamicData | undefined>();

    const currentGalleryImages = ref<
      ContentApiImage[] | SmartSearchImageUrl[]
    >();

    // Actions

    function setHotelStaticData(initialHotelStaticData: HotelStaticData): void {
      hotelStaticData.value = initialHotelStaticData;
    }

    function setHotelDynamicData(
      initialHotelDynamicData: HotelDynamicData
    ): void {
      hotelDynamicData.value = initialHotelDynamicData;
    }

    function setGalleryImages(
      images?: ContentApiImage[] | SmartSearchImageUrl[]
    ): void {
      currentGalleryImages.value = images;
    }

    // Getters
    const headerData = computed<HotelDetailsHeaderData | undefined>(() => {
      if (hotelStaticData.value) {
        return {
          headerText: hotelStaticData.value.staticInfo.name,
          starsAmount: hotelStaticData.value.staticInfo.semboSunRating,
          isHearted: hotelStaticData.value.isHearted,
          subHeaderText: hotelStaticData.value.staticInfo.mainPolygon.fullName,
        };
      }
      return undefined;
    });

    const galleryType = computed<GalleryType>(() =>
      browserStore.isSmallerThan1200px
        ? GalleryType.Swiper
        : GalleryType.Masonry
    );

    const hotelStays = computed<Stay[]>(() => {
      if (hotelStaticData.value && hotelDynamicData.value) {
        return getStays(
          hotelDynamicData.value.availability,
          hotelStaticData.value.numberOfNights,
          hotelStaticData.value.isInItinerary,
          globalCurrencyStore.formatMoney,
          preferredTimeStore.preferredDay
        );
      }

      return [];
    });

    const selectedStay = computed<Stay | undefined>(() =>
      getSelectedStay(hotelStays.value)
    );

    const alternativeStays = computed<Stay[]>(() =>
      hotelStays.value.filter(
        (stay) => stay.stayId !== selectedStay.value?.stayId
      )
    );

    const footerData = computed<HotelDetailsFooterData | undefined>(() =>
      getFooterData(
        hotelStaticData.value,
        selectedStay.value,
        hotelStays.value[0],
        itineraryStore.getRangeDates,
        globalCurrencyStore.formatMoney
      )
    );

    const galleryData = computed<HotelDetailsGalleryData | undefined>(() =>
      getGalleryData(currentGalleryImages.value, headerData.value?.headerText)
    );

    const selectionSectionStay = computed<Stay | undefined>(
      () => selectedStay.value ?? alternativeStays.value[0]
    );

    return {
      // State
      hotelStaticData,
      hotelDynamicData,

      // Actions
      setHotelStaticData,
      setHotelDynamicData,
      setGalleryImages,

      // Getters
      headerData,
      galleryType,
      selectedStay,
      selectionSectionStay,
      alternativeStays,
      footerData,
      galleryData,
    };
  }
);
